import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, OnInit, output, viewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { StandardConfigurationType } from '@app/cem/project/enums/standard-config-type';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogActionsComponent, DialogComponent } from '@progress/kendo-angular-dialog';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { AssetTeamId, ProjectStatusId } from 'app/shared/data/project-enums';
import { LookupPlusService } from '../services/lookups/lookup-plus.service';
import { ProjectCustomEndpointService } from '../services/project-custom-endpoint.service';

interface StandardConfiguration {
  StandardConfigurationId: number;
  Name: string | null;
  IsDefault?: boolean;
  IsDELDefault?: boolean;
  ProjectSubTypeId?: number;
  EffectiveDate?: string | Date | null;
}

export interface StandardConfigurationUpdate {
  OldStandardConfig: StandardConfiguration;
  NewStandardConfig: StandardConfiguration;
}

@Component({
  selector: 'app-standard-config-sync',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './standard-config-sync.component.html',
  styleUrls: ['./standard-config-sync.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    ComboBoxComponent,
    DialogActionsComponent,
    DialogComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class StandardConfigSyncComponent implements OnInit {
  private lookupPlusService = inject(LookupPlusService);
  private projectCustomEndpointService = inject(ProjectCustomEndpointService);
  private formBuilder = inject(UntypedFormBuilder);
  private cdr = inject(ChangeDetectorRef);

  private tStandardConfiguration: string = 'StandardConfigurations';
  private sStandardConfiguration: string = '$select=StandardConfigurationId%2C%20Name%2C%20ProjectSubTypeId%2C%20IsDefault%2C%20IsDELDefault%2C%20EffectiveDate';
  private tProject: string = 'ProjectEstimates';
  private fProject: string = '?$expand=Project($select=IsStandardConfigurationSync%2C%20ProjectSubTypeId%2C%20AssetTeamId%2C%20StartDate)&$filter=ProjectEstimateId%20eq%20';
  private sProject: string = '&$select=StandardConfigurationId';

  public dStandardConfiguration = Array<StandardConfiguration>();
  public dProject = Array<any>();
  public valueStandardConfiguration: StandardConfiguration;
  public StandardConfigurationUpdateInstance: StandardConfigurationUpdate;

  public valueStandardConfigurationSync: boolean;
  public ShowConfirmDialog: boolean;
  public syncForm: UntypedFormGroup;
  public disableAllEdits: boolean;

  readonly disabled = input<boolean>(false);
  readonly iProjectId = input<number>(undefined);
  readonly iProjectEstimateId = input<number>(undefined);
  readonly iProjectTypeId = input<number>(undefined);
  readonly iProjectStatusId = input(undefined);
  readonly SyncStandardConfig = output<any>();

  readonly StandardconfigControl = viewChild<ComboBoxComponent>('standardconfig');

  constructor() {
    this.syncForm = this.formBuilder.group({

    });
  }

  private findStandardConfigValue(id: number): StandardConfiguration {
    return this.dStandardConfiguration.find(x => x.StandardConfigurationId === id);
  }

  // Devs - Add new basin mapping to new default column
  private getDefaultPredicate(assetTeamId: number): (x: StandardConfiguration) => boolean {
    if (assetTeamId === AssetTeamId.DEL) {
      return (x: StandardConfiguration) => x.IsDELDefault === true;
    } else if (assetTeamId === AssetTeamId.PAT) {
      return (x: StandardConfiguration) => x.IsDefault === true;
    } else {
      console.warn(`Unhandled AssetTeam. No default column found for the current basin.`);
      return () => false;
    }
  }

  public findDefaultConfiguration(projSubTypeId: number, assetTeamId: number, projStart: Date): StandardConfiguration {
    const projStartTime = new Date(projStart).getTime();
    const dateFiltered = this.dStandardConfiguration
      .map(x => ({ config: x, effectiveTime: x.EffectiveDate != null ? new Date(x.EffectiveDate).getTime() : null }))
      .filter(x => x.effectiveTime != null && x.effectiveTime <= projStartTime)
      .sort((a, b) => b.effectiveTime - a.effectiveTime)
      .map(x => x.config);

    const isDefault = this.getDefaultPredicate(assetTeamId);
    if (dateFiltered.length === 0) {
      return null;
    }

    const subTypeFiltered = (projSubTypeId != null && projSubTypeId !== 0)
      ? dateFiltered.filter(x => x.ProjectSubTypeId === projSubTypeId || x.ProjectSubTypeId === null)
      : dateFiltered; // no subtype provided → all records pass

    const defaults = subTypeFiltered.filter(isDefault);
    const defaultConfig = defaults.length > 0 ? defaults[0] : null;

    if (defaultConfig) {
      this.dStandardConfiguration = [
        defaultConfig,
        ...this.dStandardConfiguration.filter(x => x.StandardConfigurationId !== defaultConfig.StandardConfigurationId)
      ];
    }
    return defaultConfig;
  }

  private populateForm(value: boolean): void {
    this.syncForm.patchValue({
    });
  }

  ngOnInit() {
    const iProjectStatusId = this.iProjectStatusId();
    if (iProjectStatusId === ProjectStatusId.Closed || iProjectStatusId === ProjectStatusId.Cancelled) {
      this.disableAllEdits = true;
      this.syncForm.disable();
    } else {
      this.disableAllEdits = false;
      this.refreshStandardConfiguration();
    }
  }

  public refreshStandardConfiguration(): void {
    const iProjectTypeId = this.iProjectTypeId();
    if (iProjectTypeId) {
      let f1: string = '$filter=(ProjectTypeId%20eq%20';
      let f0: string = '%20or%20ProjectTypeId%20eq%20null)';
      let f2: string = '%20and%20IsActive%20eq%20true';
      let filter: string = `${f1}${iProjectTypeId}${f0}${f2}`;
      let url = `${filter}&${this.sStandardConfiguration}`;
      this.getStandardConfigurations(this.tStandardConfiguration, url);
    }
  }

  public getStandardConfigurations(table: string, oString: string) {
    this.lookupPlusService.getLookup(table, oString)
      .then(payload => {
        this.dStandardConfiguration = payload.value;
        const iProjectEstimateId = this.iProjectEstimateId();
        if (!iProjectEstimateId) {
          this.valueStandardConfiguration = null;
          return;
        }
        let url = `${this.tProject}${this.fProject}${iProjectEstimateId}${this.sProject}`;
        // loaded the StandardConfiguration array. Now get the project information using the ProjectId input
        // StandardConfigurationId is not a fk in Projects, so have to get the value through odata.
        this.lookupPlusService.getSingleTableRow(url)
          .then(z => {
            let obj: any;
            let log: boolean = false;
            if (z && z.value && z.value.length > 0) {
              obj = this.findStandardConfigValue(z.value[0].StandardConfigurationId);
              this.findDefaultConfiguration(z.value[0].Project.ProjectSubTypeId, z.value[0].Project.AssetTeamId, z.value[0].Project.StartDate);
              log = z.value[0].Project.IsStandardConfigurationSync;
            } else {
              obj = null;
            }
            this.valueStandardConfiguration = obj ? obj : null;
            this.populateForm(log);
            this.StandardconfigControl().value = obj;
            this.cdr.detectChanges();
          });
      });
  }

  public processStandardConfigChange(updateUi: boolean) {
    let patch = {
      StandardConfigurationId: StandardConfigurationType.None
    };
    patch.StandardConfigurationId = this.StandardConfigurationUpdateInstance.NewStandardConfig.StandardConfigurationId;
    let urlPatch = `ProjectEstimates(${this.iProjectEstimateId()})`;
    this.projectCustomEndpointService.patch(urlPatch, patch)
      .then(x => {
        this.ShowConfirmDialog = false;
        let urlPost = `estimation/projects/${this.iProjectId()}/syncStdConfig`;
        this.projectCustomEndpointService.generate(urlPost).then(y => {
          this.valueStandardConfiguration = this.StandardConfigurationUpdateInstance.NewStandardConfig;
          this.StandardconfigControl().value = this.valueStandardConfiguration;
          if (updateUi) {
            this.SyncStandardConfig.emit(this.StandardConfigurationUpdateInstance);
          }
        });
      });
  }

  public cancelStandardConfigChange() {
    let obj: any;
    obj = this.findStandardConfigValue(this.StandardConfigurationUpdateInstance.OldStandardConfig.StandardConfigurationId);
    this.valueStandardConfiguration = obj;
    this.StandardconfigControl().value = obj;
    this.ShowConfirmDialog = false;
  }

  public handleStandardConfigurationSelection(item) {
    if (item && ((this.valueStandardConfiguration === null) || (item.StandardConfigurationId !== this.valueStandardConfiguration.StandardConfigurationId))) {
      this.StandardConfigurationUpdateInstance = { NewStandardConfig: item, OldStandardConfig: this.valueStandardConfiguration };
      this.valueStandardConfiguration = item;
      this.StandardconfigControl().value = this.valueStandardConfiguration;

      if (item.StandardConfigurationId === StandardConfigurationType.Custom) {
        this.processStandardConfigChange(false);
      }
      else {
        this.ShowConfirmDialog = true;
      }
    }
  }
}